import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery, useMutation } from '@tanstack/react-query'
import axios from 'axios'
import { ErrorState } from '@/components/ui'

const FIELD_LABEL: Record<string, string> = {
  name: 'Firmenname',
  street: 'Straße',
  zipCode: 'PLZ',
  city: 'Stadt',
  country: 'Land',
  contactPhone: 'Telefon',
  website: 'Website',
  industryName: 'Branche',
}

interface FormInfo {
  recipientId: string
  fieldSet: string[]
  alreadySubmitted: boolean
}

export default function BpFormPage() {
  const { token } = useParams<{ token: string }>()
  const [form, setForm] = useState<Record<string, string>>({})
  const [submitted, setSubmitted] = useState(false)

  const { data, isLoading, isError, error } = useQuery<FormInfo>({
    queryKey: ['bp-form', token],
    queryFn: () => axios.get(`/api/public/gp-form/${token}`).then((r) => r.data as FormInfo),
    enabled: !!token,
    retry: false,
  })

  const submitMutation = useMutation({
    mutationFn: () => axios.post(`/api/public/gp-form/${token}`, form),
    onSuccess: () => setSubmitted(true),
  })

  if (isLoading) {
    return (
      <div style={{ fontFamily: 'sans-serif', maxWidth: 480, margin: '60px auto', padding: 24 }}>
        <p>Lade Formular …</p>
      </div>
    )
  }

  if (isError || !data) {
    const errAny = error as { response?: { data?: { message?: string }; status?: number } } | null
    const errMsg = errAny?.response?.data?.message ?? 'Dieser Link ist nicht mehr gültig. Bitte wende dich an deinen Ansprechpartner.'
    return (
      <div style={{ fontFamily: 'sans-serif', maxWidth: 480, margin: '60px auto', padding: 24 }}>
        <ErrorState
          title="Link ungültig oder abgelaufen"
          description={errMsg}
          details={error ? JSON.stringify(error, Object.getOwnPropertyNames(error), 2) : undefined}
        />
      </div>
    )
  }

  if (data.alreadySubmitted || submitted) {
    return (
      <div style={{ fontFamily: 'sans-serif', maxWidth: 480, margin: '60px auto', padding: 24, textAlign: 'center' }}>
        <h2 style={{ color: '#16a34a' }}>Vielen Dank!</h2>
        <p style={{ color: '#6b7280' }}>Ihre Daten wurden erfolgreich übermittelt.</p>
      </div>
    )
  }

  return (
    <div style={{ fontFamily: 'sans-serif', maxWidth: 480, margin: '60px auto', padding: 24 }}>
      <h1 style={{ color: '#1e40af', marginBottom: 8 }}>Stammdaten aktualisieren</h1>
      <p style={{ color: '#6b7280', marginBottom: 24 }}>
        Bitte überprüfen Sie Ihre Angaben und aktualisieren Sie bei Bedarf.
      </p>

      <form onSubmit={(e) => { e.preventDefault(); submitMutation.mutate() }}>
        {data.fieldSet.map((field) => (
          <div key={field} style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 4, color: '#374151' }}>
              {FIELD_LABEL[field] ?? field}
            </label>
            <input
              type="text"
              value={form[field] ?? ''}
              onChange={(e) => setForm((p) => ({ ...p, [field]: e.target.value }))}
              style={{
                width: '100%', padding: '8px 12px', border: '1px solid #d1d5db',
                borderRadius: 6, fontSize: 14, boxSizing: 'border-box',
              }}
            />
          </div>
        ))}

        <button
          type="submit"
          disabled={submitMutation.isPending}
          style={{
            background: '#1e40af', color: 'white', padding: '10px 24px',
            border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: 600,
          }}
        >
          {submitMutation.isPending ? 'Wird gesendet …' : 'Daten übermitteln'}
        </button>

        {submitMutation.isError && (
          <p style={{ color: '#dc2626', marginTop: 12 }}>
            Fehler beim Speichern. Bitte versuche es erneut.
          </p>
        )}
      </form>
    </div>
  )
}
